import { safeSessionStorage } from 'src/lib/browserStorage';

const RESET_CORRELATION_ID_STORAGE_KEY = 'auth_reset_correlation_id';

export interface AuthInstrumentationHeaderOptions {
    flow: string;
    popupMode?: 'redirect' | 'popup';
    popupAttempted?: boolean;
    popupOpened?: boolean;
    includeResetCorrelationId?: boolean;
}

const generateCorrelationId = (): string => {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    const bytes = globalThis.crypto?.getRandomValues?.(new Uint8Array(16));
    if (bytes) {
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const getOrCreateResetCorrelationId = (): string => {
    const existing = safeSessionStorage.getItem(RESET_CORRELATION_ID_STORAGE_KEY);
    if (existing) {
        return existing;
    }
    const generated = generateCorrelationId();
    safeSessionStorage.setItem(RESET_CORRELATION_ID_STORAGE_KEY, generated);
    return generated;
};

export const clearResetCorrelationId = (): void => {
    safeSessionStorage.removeItem(RESET_CORRELATION_ID_STORAGE_KEY);
};

export const buildAuthInstrumentationHeaders = (
    options: AuthInstrumentationHeaderOptions,
): Record<string, string> => {
    const headers: Record<string, string> = {
        'X-Auth-Flow': options.flow,
        'X-Auth-Correlation-Id': generateCorrelationId(),
    };

    if (options.popupMode) {
        headers['X-Auth-Popup-Mode'] = options.popupMode;
    }
    if (options.popupAttempted !== undefined) {
        headers['X-Auth-Popup-Attempted'] = String(options.popupAttempted);
    }
    if (options.popupOpened !== undefined) {
        headers['X-Auth-Popup-Opened'] = String(options.popupOpened);
    }

    if (options.includeResetCorrelationId) {
        headers['X-Auth-Reset-Correlation-Id'] = getOrCreateResetCorrelationId();
    }

    return headers;
};
